/**
 * Date-validation-fixes batch (2026-07-23): the ONE shared date-rules
 * implementation, used at all three call sites (Answers, Lines, the
 * RFP-generation gate) plus resume revalidation - previously each had its
 * own slightly-diverged copy (line-level treated `iso == deadline` as
 * valid; Answers-level treated it as invalid; no soft-alert concept
 * existed at Lines at all). One function, one set of rules.
 *
 * Rule codes (confirmed with Ashok - HARD stops block, SOFT alerts warn):
 *   HVPT - submission deadline itself is in the past
 *   HVND / HVSD - need-by (goods) / start (services) date is in the past
 *   HVES - end date before start date (services). Equality (start==end,
 *     a same-day service) is VALID - deliberately not flagged.
 *   HVPN / HVPS - submission deadline on-or-after need-by/start (must be
 *     STRICTLY after submission) - goods/services variants.
 *   SAPT - submission vs today gap < 7 days (stays hardcoded forever -
 *     never becomes an admin param, unlike the other two soft pairs below).
 *   SANT / SAST - need-by/start vs TODAY gap < 7 days (goods/services) -
 *     future "Required-by-Date Param" admin param, deferred; computed here
 *     and unit-tested, but not yet surfaced in any UI (per Ashok: surfaced
 *     at Lines' per-line consolidation, not at Answers, in this batch).
 *   SAPN / SAPS - submission vs need-by/start gap < 7 days (goods/services)
 *     - future "Contracting Cycle Time" admin param, deferred (both
 *     default 7 - hardcoded in DATE_RULE_THRESHOLDS below so a future
 *     admin-params batch can source them from config without touching any
 *     call site).
 * There is deliberately NO standalone "end date in the past" rule - HVSD
 * (start not in the past) + HVES (end >= start) already imply the end date
 * can't be in the past either.
 *
 * Consolidated fix batch (2026-07-24), item 1 - message content audit
 * against the master table:
 *   Message 1 (SANT/SAST): required date too close to TODAY - compresses
 *     the ENTIRE downstream cycle (submission, evaluation, award,
 *     onboarding, delivery), not just vendor response time.
 *   Message 2 (SAPT): submission deadline too close to TODAY - vendors may
 *     not have time to prepare/submit proposals AND raise queries.
 *   Message 3 (SAPN/SAPS): required date too close to the SUBMISSION
 *     DEADLINE (not today) - insufficient runway for RFP finalization,
 *     evaluation, award/onboarding, and vendor lead time. This was
 *     previously worded like Message 2's concept (vendor prep time) - a
 *     confirmed content bug, now fixed with the exact master-table copy.
 * Messages 1/2 had no exact master-table text on file - rewritten here to
 * match the DESCRIBED content (not a verbatim quote like Message 3); flag
 * for Ashok to confirm/adjust wording if it doesn't match his table.
 *
 * Item 2 - every message (hard AND soft) gets its rule code appended as a
 * suffix, via formatRuleCodeSuffix/appendRuleCodeSuffix below - the ONE
 * shared "message builder" for this, so every call site (Answers dialogs,
 * Lines inline/consolidated text, the Generate summary dialog, toasts)
 * uses the same formatting instead of ad-hoc bracket-string-building.
 * Messages themselves are returned WITHOUT the suffix baked in (`code` is
 * a separate field on every hardStop/softAlert) - callers append it
 * themselves, since dialog surfaces render the suffix in a visually muted
 * span (a separate JSX element) while non-dialog surfaces (toasts, inline
 * FieldError, Lines' per-line hover/pin text) just concatenate plain text.
 *
 * Generate-summary polish (2026-07-27): urgency framing ("...and is an
 * urgent request") is reserved for Message 1 (SANT/SAST) alone - Messages
 * 2 (SAPT) and 3 (SAPN/SAPS) are procedural-window concerns, not urgency,
 * and now end on "Proceed if this is intended." without the urgent-
 * request clause. Severity is two-tier only (HIGH/MEDIUM, no LOW) -
 * SAPT shares MEDIUM with SAPN/SAPS; their relative order within that
 * tier is a fixed rule-order tiebreak (SAPN/SAPS before SAPT), not a
 * severity distinction - see SOFT_ALERT_SEVERITY/sortBySeverity below.
 */

export const DATE_RULE_THRESHOLDS = {
  SAPT_DAYS: 7,
  SAPN_SAPS_DAYS: 7,
  SANT_SAST_DAYS: 7,
};

// SOFT-ALERT SEVERITY BATCH (2026-07-25, corrected 2026-07-27): intrinsic
// per-rule severity, defined ONCE here (mirrored in
// _date_validation_rules.py) - every surface (Answers dialogs, Lines
// per-line consolidation, the Generate summary dialog) inherits severity +
// ordering from this map alone, never redefines its own.
//
// TWO tiers only - there is no LOW:
//   HIGH   - SANT/SAST: need-by/start within days of TODAY - the entire
//            downstream cycle (publish, evaluate, award, onboard, plus
//            delivery/mobilization) must fit inside the window.
//   MEDIUM - SAPN/SAPS (post-submission runway short) AND SAPT (vendor
//            response window short) - both are procedural-window
//            concerns, not urgency, so they share a tier.
// Stated-intent echo checks (Ashok ruling, 2026-07-25 - see
// lib/statedIntentValidationRules.js): SARV/SARD join this SAME severity/
// order map rather than defining their own, so a chain mixing date rules
// and stated-intent rules in the same Answers-submit attempt sorts
// consistently - MEDIUM, ordered after SAPN/SAPS (both concern the
// required-date/submission relationship) and before SAPT (vendor-response
// window - the least specific of the MEDIUM group). SAQD (Stated-Answer
// Quantity/Dates - StepAnswers.jsx's pre-existing typed-quantity-vs-dates
// confirm, formalized with its own code per the code-in-message audit)
// joins right after SARD - same cluster of "quantity/dates cooperation"
// concerns, still ahead of SAPT. SARU (MIXED-SEMANTICS + ECHO FIX BATCH,
// item D - cross-dimension unit echo, ALL procurement types unlike
// SARV/SARD) joins the same tier, ordered right before SARV since it is
// the more specific/root-cause check when both would otherwise flag the
// same answered-quantity fact (see statedIntentValidationRules.js's
// checkStatedUnitDimensionEcho doc for the de-dup convention).
export const SOFT_ALERT_SEVERITY = {
  SANT: 'HIGH',
  SAST: 'HIGH',
  SAPN: 'MEDIUM',
  SAPS: 'MEDIUM',
  SARU: 'MEDIUM',
  SARV: 'MEDIUM',
  SARD: 'MEDIUM',
  SAQD: 'MEDIUM',
  SAPT: 'MEDIUM',
};

// Ascending = most severe first, so a plain numeric sort puts HIGH before
// MEDIUM.
const SEVERITY_RANK = { HIGH: 0, MEDIUM: 1 };

export const severityRank = (severity) => SEVERITY_RANK[severity] ?? 99;

// SAPN/SAPS and SAPT share the MEDIUM tier, but their relative order is
// still fixed by rule (not severity) - SAPN/SAPS always before SAPT, in
// the chain, Lines' consolidated code lists, and the Generate summary.
// This is a tiebreak for codes with EQUAL severity, applied after
// severityRank in both leadingCode and sortBySeverity below - it is
// deliberately NOT itself a severity.
const CODE_ORDER = ['SANT', 'SAST', 'SAPN', 'SAPS', 'SARU', 'SARV', 'SARD', 'SAQD', 'SAPT'];
const codeOrderRank = (code) => {
  const idx = CODE_ORDER.indexOf(code);
  return idx === -1 ? CODE_ORDER.length : idx;
};

const compareCodes = (codeA, codeB) => {
  const rankDiff = severityRank(SOFT_ALERT_SEVERITY[codeA]) - severityRank(SOFT_ALERT_SEVERITY[codeB]);
  return rankDiff !== 0 ? rankDiff : codeOrderRank(codeA) - codeOrderRank(codeB);
};

// The single most relevant code among a list of rule codes - by severity,
// then the fixed rule-order tiebreak above - used to pick one row's sort
// key / one chip's severity for a line that carries multiple codes.
export const leadingCode = (codes) => {
  const list = (codes || []).filter((c) => SOFT_ALERT_SEVERITY[c]);
  if (list.length === 0) return null;
  return [...list].sort(compareCodes)[0];
};

// The single most severe severity among a list of rule codes - used to
// pick one chip for a row/line that carries multiple contributing codes.
export const highestSeverity = (codes) => {
  const code = leadingCode(codes);
  return code ? SOFT_ALERT_SEVERITY[code] : 'MEDIUM';
};

// Sorts an array of {code, ...} objects most-severe-first, with the fixed
// rule-order tiebreak above applied within a shared severity tier (e.g.
// SAPN before SAPT). Array#sort is stable per spec, so genuinely-equal
// items (same code) keep their original relative order.
export const sortBySeverity = (items, codeKey = 'code') =>
  [...(items || [])].sort((a, b) => compareCodes(a[codeKey], b[codeKey]));

// YYYY-MM-DD -> DD-MMM-YYYY, for embedding in message text. Independently
// duplicated (not imported from lib/utils.js's formatDate) to keep this
// module's only import surface limited to what it already needs - same
// "mirrors X, doesn't depend on it" convention used elsewhere in this
// codebase (e.g. _line_item_validation.py's _format_date_display).
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDisplayDate = (iso) => {
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return iso;
  return `${day}-${MONTH_ABBR[monthIndex]}-${year}`;
};

// Item 2 - the one shared suffix builder. `codes` may be a single code
// string or an array (Lines' per-line consolidation combines every
// contributing rule into one trailing bracket, e.g. "[SANT, SAPN]").
export const formatRuleCodeSuffix = (codes) => {
  const list = Array.isArray(codes) ? codes : [codes];
  return `[${list.join(', ')}]`;
};

export const appendRuleCodeSuffix = (message, codes) => `${message} ${formatRuleCodeSuffix(codes)}`;

// YYYY-MM-DD, so string comparison is chronological - matches the backend's
// _parse_iso_date/today_iso convention exactly.
export const toIsoDateOrNull = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// goods|services for date-rule purposes. A line's own item_type wins when
// present (set by refinement_agent for mixed procurements); else the
// doc-level procurementType when unambiguous; else (mixed, no item_type on
// this line) infer from which date fields are already populated. Moved
// here from lib/lineItemValidation.js (dependency-free target, same
// extraction pattern as lib/businessQuestions.js in the prior batch) -
// lineItemValidation.js re-exports both names unchanged.
export const effectiveItemType = (item, procurementType) => {
  if (item.item_type === 'goods' || item.item_type === 'services') return item.item_type;
  if (procurementType === 'goods' || procurementType === 'services') return procurementType;
  if (item.start_date || item.end_date) return 'services';
  return 'goods';
};

export const classifyLineItems = (lineItems, procurementType) =>
  (lineItems || []).map((item) => effectiveItemType(item, procurementType));

// EXECUTION SCHEDULE MODEL (Ashok's design, 2026-07-26): BUYER_DEFINED or
// VENDOR_PROPOSED for a service line - mirrors effectiveItemType's pattern
// (backend: _date_validation_rules.py's effective_schedule_mode).
// Unmarked/legacy lines default to BUYER_DEFINED - every existing line, and
// every date rule above, is unaffected by this field's introduction.
export const effectiveScheduleMode = (item) =>
  item?.schedule_mode === 'VENDOR_PROPOSED' ? 'VENDOR_PROPOSED' : 'BUYER_DEFINED';

const oneDayMs = 1000 * 60 * 60 * 24;
const diffDays = (laterIso, earlierIso) => Math.round((new Date(laterIso) - new Date(earlierIso)) / oneDayMs);

/**
 * Core rule evaluation. `itemType` is optional: when omitted (a
 * submission-only call - the Lines-step submission reference box, or the
 * gate's "is the deadline itself stale" check), only HVPT/SAPT are
 * evaluated - no needBy/start/end is required for that call shape.
 *
 * Returns { hardStops: [{code, field, message}], softAlerts:
 * [{code, field, message, gapDays}] }. `field` is one of
 * 'submission_deadline' | 'need_by_date' | 'start_date' | 'end_date' -
 * matches the real field_key/line-item-key names directly so callers never
 * need a translation layer.
 */
export const checkDateRules = ({ itemType, needBy, start, end, submission, now = new Date() }) => {
  const hardStops = [];
  const softAlerts = [];

  const todayIso = toIsoDateOrNull(now);
  if (!todayIso) return { hardStops, softAlerts }; // fail open on a bad clock

  const submissionIso = toIsoDateOrNull(submission);
  const needByIso = itemType === 'goods' ? toIsoDateOrNull(needBy) : null;
  const startIso = itemType === 'services' ? toIsoDateOrNull(start) : null;
  const endIso = itemType === 'services' ? toIsoDateOrNull(end) : null;

  // The need-by/start date driving the submission-relative rules
  // (HVPN/HVPS/SANT/SAST/SAPN/SAPS).
  const requiredIso = itemType === 'services' ? startIso : needByIso;
  const requiredField = itemType === 'services' ? 'start_date' : 'need_by_date';
  const requiredLabel = itemType === 'services' ? 'Start date' : 'Need by date';

  // HVPT - submission itself in the past.
  if (submissionIso && submissionIso < todayIso) {
    hardStops.push({
      code: 'HVPT',
      field: 'submission_deadline',
      message: 'Submission deadline must be a future date. Please update it to continue.',
    });
  }

  // HVND / HVSD - required date in the past.
  if (requiredIso && requiredIso < todayIso) {
    hardStops.push({
      code: itemType === 'services' ? 'HVSD' : 'HVND',
      field: requiredField,
      message: `${requiredLabel} must be a future date. Please update it to continue.`,
    });
  }

  // HVES - end strictly before start (services). Equality (a same-day
  // service) is deliberately valid.
  if (itemType === 'services' && startIso && endIso && startIso > endIso) {
    hardStops.push({
      code: 'HVES',
      field: 'end_date',
      message: 'End date must be on or after the start date.',
    });
  }

  const requiredFieldHasHardStop = () => hardStops.some((h) => h.field === requiredField);

  // HVPN / HVPS - required date on-or-before submission (must be strictly
  // after) - skipped if the required date is already flagged in the past
  // (HVND/HVSD), since that's the more fundamental problem to fix first.
  if (submissionIso && requiredIso && requiredIso <= submissionIso && !requiredFieldHasHardStop()) {
    hardStops.push({
      code: itemType === 'services' ? 'HVPS' : 'HVPN',
      field: requiredField,
      message: itemType === 'services'
        ? 'Service Start Date must be after the submission deadline. Vendors need time to respond before work begins.'
        : 'Select Proposal Submission which is before the Need by Date.',
    });
  }

  const submissionHasHardStop = hardStops.some((h) => h.field === 'submission_deadline');

  // required-date noun, lowercase, for mid-sentence use in message copy.
  const requiredNounLower = itemType === 'services' ? 'start date' : 'need-by date';

  // SAPT - submission close to today. Message 2: vendor time to prepare,
  // submit proposals, AND raise queries - not just "submit". Generate-
  // summary polish (2026-07-27): urgency framing ("...and is an urgent
  // request") is reserved for Message 1 (SANT/SAST) only - SAPT is a
  // procedural-window concern, not urgency, so it ends on "Proceed if
  // this is intended." alone.
  if (submissionIso && !submissionHasHardStop) {
    const gap = diffDays(submissionIso, todayIso);
    if (gap >= 0 && gap < DATE_RULE_THRESHOLDS.SAPT_DAYS) {
      softAlerts.push({
        code: 'SAPT',
        field: 'submission_deadline',
        gapDays: gap,
        severity: SOFT_ALERT_SEVERITY.SAPT,
        message: `Your submission deadline (${formatDisplayDate(submissionIso)}) is only ${gap} day${gap === 1 ? '' : 's'} from today — vendors may not have sufficient time to prepare, submit their proposals, and raise queries. Proceed if this is intended.`,
      });
    }
  }

  // SANT / SAST - required date close to today. Message 1: compresses the
  // ENTIRE downstream cycle (submission through delivery), not just vendor
  // response time.
  if (requiredIso && !requiredFieldHasHardStop()) {
    const gap = diffDays(requiredIso, todayIso);
    if (gap >= 0 && gap < DATE_RULE_THRESHOLDS.SANT_SAST_DAYS) {
      const sanSastCode = itemType === 'services' ? 'SAST' : 'SANT';
      softAlerts.push({
        code: sanSastCode,
        field: requiredField,
        gapDays: gap,
        severity: SOFT_ALERT_SEVERITY[sanSastCode],
        message: `You have selected a ${requiredNounLower} (${formatDisplayDate(requiredIso)}) which is only ${gap} day${gap === 1 ? '' : 's'} from today — this compresses the entire downstream cycle: submission deadline, vendor evaluation, award, onboarding, and delivery lead time. Proceed if this is intended and is an urgent request.`,
      });
    }
  }

  // SAPN / SAPS - required date close to submission. Message 3 - NOT
  // vendor prep time (that's Message 2/SAPT); this is about the runway
  // AFTER submission closes. Generate-summary polish (2026-07-27): drops
  // the urgent-request clause (reserved for Message 1/SANT-SAST only) -
  // ends on "...and the vendor's own lead time. Proceed if this is
  // intended." instead.
  if (submissionIso && requiredIso && !requiredFieldHasHardStop()) {
    const gap = diffDays(requiredIso, submissionIso);
    if (gap >= 0 && gap < DATE_RULE_THRESHOLDS.SAPN_SAPS_DAYS) {
      const sapnSapsCode = itemType === 'services' ? 'SAPS' : 'SAPN';
      softAlerts.push({
        code: sapnSapsCode,
        field: requiredField,
        gapDays: gap,
        severity: SOFT_ALERT_SEVERITY[sapnSapsCode],
        message: `You have selected a ${requiredNounLower} (${formatDisplayDate(requiredIso)}) which is only ${gap} day${gap === 1 ? '' : 's'} after the submission deadline (${formatDisplayDate(submissionIso)}) — this may not be sufficient for completing downstream activities: finalizing & publishing this RFP, receiving & evaluating vendor proposals, awarding and onboarding the vendor, and the vendor's own lead time. Proceed if this is intended.`,
      });
    }
  }

  return { hardStops, softAlerts };
};

/**
 * Convenience wrapper for a line-item object (need_by_date/start_date/
 * end_date keys) - used by lineItemValidation.js, LineItemsEditor.jsx, and
 * WizardContext.jsx's resume check. `itemTypeOverride`, if given, is used
 * instead of re-deriving effectiveItemType (Defect A fix precedent - pass
 * the caller's already-computed classification so this never disagrees
 * with what the caller decided to render).
 */
export const checkLineItemDateRules = (
  item,
  { procurementType, submissionDeadline, now, itemTypeOverride } = {}
) => {
  const itemType = itemTypeOverride || effectiveItemType(item, procurementType);
  return checkDateRules({
    itemType,
    needBy: item.need_by_date,
    start: item.start_date,
    end: item.end_date,
    submission: submissionDeadline,
    now,
  });
};
